import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {
  isLoaded as isLoadedProducts,
  Plan,
  PlanModel,
  UnassignedPlanModel,
} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import {Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import * as RI from '../RiderInformation';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

const riderCodes: RI.OrdinaryRiderCode[] = [
  {
    code: 'ICR',
    displayName: '',
    units: [1, 2, 3, 4, 5, 6, 7, 8],
  },
  {
    code: 'CID',
    displayName: '',
    units: [0.5, 1.0],
  },
  {
    code: 'WBR',
    displayName: '',
    units: [1, 2, 3, 4],
  },
  {
    code: 'WBR3',
    displayName: '',
    units: [1, 2, 3, 4, 5],
  },
];

type HeartStrokeProps = {
  productType: StringUnion;
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  section125: boolean;
  replacement: boolean;
  policyNumber: PolicyNumber;
  riderInformation: RI.default;
  productList: PlanModel;
  overrideBillForm: boolean;
  baseUnits: StringUnion;
};

export class InvalidOrdinaryHeartStroke {
  public readonly key = 'InvalidOrdinaryHeartStroke';

  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly riderInformation: RI.default,
    public readonly productList: PlanModel,
    public readonly overrideBillForm: boolean,
    public readonly baseUnits: StringUnion
  ) {}
}

export class ValidOrdinaryHeartStroke {
  public static createApp({
    productType,
    planType,
    premium,
    calculatedPremium,
    cwa,
    section125,
    replacement,
    policyNumber,
    riderInformation,
    productList,
    overrideBillForm,
    baseUnits,
  }: HeartStrokeProps) {
    return this.create(
      productType,
      planType,
      premium,
      calculatedPremium,
      cwa,
      section125,
      replacement,
      policyNumber,
      riderInformation,
      productList,
      overrideBillForm,
      baseUnits
    );
  }

  public static create(
    productType: StringUnion,
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    section125: boolean,
    replacement: boolean,
    policyNumber: PolicyNumber,
    riderInformation: RI.default,
    productList: PlanModel,
    overrideBillForm: boolean,
    baseUnits: StringUnion
  ) {
    return isNonEmptyString(productType) &&
      planType &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      section125 &&
      replacement &&
      policyNumber &&
      riderInformation.codes.size > 0 &&
      riderInformation.amounts.size > 0 &&
      isLoadedProducts(productList)
      ? new ValidOrdinaryHeartStroke(
          productType,
          planType,
          premium,
          calculatedPremium,
          cwa,
          section125,
          replacement,
          policyNumber,
          riderInformation,
          productList,
          overrideBillForm,
          baseUnits
        )
      : new InvalidOrdinaryHeartStroke(
          productType,
          planType,
          premium,
          calculatedPremium,
          cwa,
          section125,
          replacement,
          policyNumber,
          riderInformation,
          productList,
          overrideBillForm,
          baseUnits
        );
  }

  public readonly key = 'ValidOrdinaryHeartStroke';
  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly riderInformation: RI.default,
    public readonly productList: Loaded<Plan[]>,
    public readonly overrideBillForm: boolean,
    public readonly baseUnits: StringUnion
  ) {}
}

export type OrdinaryHeartStroke = InvalidOrdinaryHeartStroke | ValidOrdinaryHeartStroke;

export type OrdinaryHeartStrokeOptions<TReturn> = ToPartialMatchOptions<OrdinaryHeartStroke, 'key', TReturn>;

export const matchOrdinaryHeartStroke = <TReturn>(options: OrdinaryHeartStrokeOptions<TReturn>) => (
  app: OrdinaryHeartStroke
): TReturn => {
  switch (app.key) {
    case 'InvalidOrdinaryHeartStroke':
      return options.InvalidOrdinaryHeartStroke ? options.InvalidOrdinaryHeartStroke(app) : options.default(app);
    case 'ValidOrdinaryHeartStroke':
      return options.ValidOrdinaryHeartStroke ? options.ValidOrdinaryHeartStroke(app) : options.default(app);
  }
};

export const EmptyOrdinaryHeartStroke = new InvalidOrdinaryHeartStroke(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  EmptyPolicyNumber,
  new RI.default(I.List<string>(), I.List<number>(), riderCodes),
  new UnassignedPlanModel(),
  false,
  new EmptyString()
);

export const changeProductType = (productType: string) => (app: OrdinaryHeartStroke) =>
  ValidOrdinaryHeartStroke.createApp({
    ...app,
    productType: NonEmptyString.create(productType),
    calculatedPremium: new EmptyString(),
    premium: new EmptyString(),
    cwa: new EmptyString(),
    policyNumber: EmptyPolicyNumber,
  });

export const changePlanType = (planType: string) => (app: OrdinaryHeartStroke) =>
  ValidOrdinaryHeartStroke.createApp({
    ...app,
    planType: NonEmptyString.create(planType),
    calculatedPremium: new EmptyString(),
    premium: new EmptyString(),
    cwa: new EmptyString(),
    policyNumber: EmptyPolicyNumber,
  });

export const changePremium = (premium: string) => (app: OrdinaryHeartStroke) =>
  ValidOrdinaryHeartStroke.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: OrdinaryHeartStroke) =>
  ValidOrdinaryHeartStroke.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: OrdinaryHeartStroke) =>
  ValidOrdinaryHeartStroke.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeSection125 = (section125: boolean) => (app: OrdinaryHeartStroke) =>
  ValidOrdinaryHeartStroke.createApp({...app, section125});

export const changeReplacement = (replacement: boolean) => (app: OrdinaryHeartStroke) =>
  ValidOrdinaryHeartStroke.createApp({...app, replacement, policyNumber: ValidPolicyNumber.create(new EmptyString())});

export const changePolicyNumber = (policyNumber: StringUnion) => (app: OrdinaryHeartStroke) =>
  ValidOrdinaryHeartStroke.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changeProductList = (products: PlanModel) => (app: OrdinaryHeartStroke) =>
  ValidOrdinaryHeartStroke.createApp({...app, productList: products});

export const changeOverrideBillForm = (overrideBillForm: boolean) => (app: OrdinaryHeartStroke) =>
  ValidOrdinaryHeartStroke.createApp({...app, overrideBillForm});

export const changeBaseUnits = (baseUnits: string) => (app: OrdinaryHeartStroke) =>
  ValidOrdinaryHeartStroke.createApp({...app, baseUnits: NonEmptyString.create(baseUnits)});

export const changeRiderInformation = (riderAmount: number, riderCode: string, index: number) => (
  app: OrdinaryHeartStroke
) => {
  const riderInformation = RI.default.changeVariableRiderInformation(
    riderAmount,
    riderCode,
    index,
    app.riderInformation.amounts,
    app.riderInformation.codes
  );

  return ValidOrdinaryHeartStroke.createApp({
    ...app,
    riderInformation: new RI.default(riderInformation.riderCodes, riderInformation.riderAmounts, riderCodes),
  });
};

export const baseUnits = [0.5, 1.0];

export const typeList = ['HSP2'];

export const getAllRiderCodes = () => riderCodes;
